import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
    ViewChild,
    DestroyRef
} from "@angular/core";
import {
    takeUntilDestroyed
} from "@angular/core/rxjs-interop";
import {
    MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions
} from "@angular/material/dialog";

import {
    SupportRequestUrgency,
    SupportSubmission,
    User
} from "@shopliftr/common-js/shared";

import { EnumUtil } from "@shopliftr/common-js/data";
import { MatTooltip } from "@angular/material/tooltip";
import { EnumDisplayPipe } from "../../pipes/enum-display/enum-display.pipe";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { IAppService } from "../../application/interfaces/app-service";
import { SupportApiClientService } from "../../iapi-application/services/api-client/support/support-api-client.service";
import { EnhancedFormDirective } from "../../forms/enhanced-form.directive";
import { MatButton } from "@angular/material/button";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import {
    MatFormField, MatLabel, MatSelect, MatSuffix
} from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { ICommonConfig } from "../../application/interfaces/common-config";
import { COMMON_APP_CONFIG } from "../../application/constants/app-tokens";
import { CdkScrollable } from "@angular/cdk/scrolling";
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { MatError } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { DialogContentContainerDirective } from "../../directives/dialog-content-container/dialog-content-container.directive";
import { ISubmitFormDialog } from "../../forms/interfaces/submit-form-dialog";
import { DialogCloseButtonDirective } from "../../application/directives/button/dialog-close-button.directive";
import { ButtonDirective } from "../../application/directives/button/button.directive";
import { ClearButtonComponent } from "../../components/shared/clear-button/clear-button.component";
import { EnhancedControlDirective } from "../../forms/directives/form-control/enhanced-control.directive";
import { ErrorMessageComponent } from "../../components/error-message/error-message.component";
import { RxjsUtil } from "../../utils/rxjs-util";

/** Type definition of the support request form group controls. */
interface ISupportRequestFormGroup {
    summary: FormControl<string>;
    description: FormControl<string>;
    urgency: FormControl<SupportRequestUrgency>;
}

/**
 * Dialog component for submitting support requests via the JIRA REST API. The dialog allows the user to
 * enter basic information about an issue they are experiencing in the application. User and application
 * context is determined automatically and added to the payload for the request.
 *
 * Implements the Submit Form — Dialog pattern ({@link ISubmitFormDialog}): a reactive
 * {@link FormGroup} holds the summary, description and urgency, and the unsaved-changes check
 * runs through the {@link EnhancedFormDirective}.
 *
 * @example
 * ```typescript
 * // Opened via SupportRequestDialogService:
 * this._supportRequestDialogService.open();
 * ```
 */
@Component({
    selector: "mgd-support-request",
    templateUrl: "./support-request.component.html",
    styleUrls: ["./support-request.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [],
    imports: [
        MatDialogTitle,
        ReactiveFormsModule,
        EnhancedFormDirective,
        CdkScrollable,
        MatDialogContent,
        MatFormField,
        MatLabel,
        MatInput,
        MatSuffix,
        MatError,
        CdkTextareaAutosize,
        MatSelect,
        MatOption,
        MatTooltip,
        EnumDisplayPipe,
        MatDialogActions,
        MatButton,
        DialogContentContainerDirective,
        ButtonDirective,
        DialogCloseButtonDirective,
        ClearButtonComponent,
        EnhancedControlDirective,
        ErrorMessageComponent
    ]
})
export class SupportRequestComponent implements OnInit, ISubmitFormDialog {

    /**
     * This is the second dirty state checked
     *
     * If there is an Enhanced Form on in the component use this variable as a content child.
     *
     * If the form is dirty, the guard will prompt the user before navigating away
     */
    @ViewChild(EnhancedFormDirective) form: EnhancedFormDirective;

    readonly dialogTitle = "Report a Problem";

    /**
     * This will instruct the guard to skip the dirty check.
     *
     * Set to true after the form has been submitted.
     */
    skipCheck = false;

    submission: SupportSubmission;

    /**
     * Expose the SupportRequestUrgency enum for template access
     */
    readonly SupportRequestUrgency = SupportRequestUrgency;

    /**
     * Urgency options, in declaration order (Critical, High, Medium, Low)
     */
    readonly urgencies = EnumUtil.getValues(SupportRequestUrgency) as Array<SupportRequestUrgency>;

    /**
     * Tooltip shown for each urgency, telling the user when Support will be alerted
     */
    readonly urgencyTooltips: Readonly<Record<SupportRequestUrgency, string>> = {
        [SupportRequestUrgency.Critical]: "Support is alerted immediately",
        [SupportRequestUrgency.High]: "Support is alerted during business hours",
        [SupportRequestUrgency.Medium]: "Support is alerted during business hours",
        [SupportRequestUrgency.Low]: "Support is alerted during business hours"
    };

    /**
     * The reactive form group for the request. Urgency is constructed with High so the default is
     * part of the unchanged snapshot, keeping the form pristine until the user edits it.
     */
    readonly formGroup = new FormGroup<ISupportRequestFormGroup>({
        summary: new FormControl<string>(undefined, [Validators.required]),
        description: new FormControl<string>(undefined, [Validators.required]),
        urgency: new FormControl<SupportRequestUrgency>(SupportRequestUrgency.High, [Validators.required])
    });

    private _currentUser: User;

    private readonly _dialogRef: MatDialogRef<SupportRequestComponent, void> = inject(MatDialogRef);
    private readonly _app = inject(COMMON_APP_SERVICE) as IAppService;
    private readonly _appConfig: ICommonConfig = inject(COMMON_APP_CONFIG);
    private readonly _supportClient = inject(SupportApiClientService);
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Initialize all static data on the support submission payload.
     */
    ngOnInit(): void {

        this.submission = new SupportSubmission().setDefaults();
        this._currentUser = this._app.authService.authenticatedUser;

        this.submission.application = this._appConfig.appName,
        this.submission.applicationVersion = this._appConfig.appVersion;
        this.submission.applicationUrl = window.location.href;
        this.submission.userId = this._currentUser.id;
        this.submission.userName = `${this._currentUser.firstName ?? ""} ${this._currentUser.lastName ?? ""}`.trim();
        this.submission.userEmail = this._currentUser.email;
        this.submission.userAgent = window.navigator.userAgent;
    }

    /**
     * Handle the click event on the submit button.
     */
    onSubmitClick(): void {

        this._handleSubmit();
    }

    /**
     * Handle the ngSubmit event on the form.
     */
    onNgSubmit(): void {

        this._handleSubmit();
    }

    /**
     * Process the form submission — shared by both the (ngSubmit) and (click) handlers.
     */
    private _handleSubmit(): void {

        if (this.form?.canSubmit) {
            this.submission.summary = this.formGroup.controls.summary.value;
            this.submission.description = this.formGroup.controls.description.value;
            this.submission.urgency = this.formGroup.controls.urgency.value;
            this.skipCheck = true;
            this._supportClient.submitTicket$(this.submission.clone())
                .pipe(
                    RxjsUtil.appProcessingUntilDone(this._app),
                    takeUntilDestroyed(this._destroyRef)
                )
                .subscribe({
                    next: (ticket) => {
                        this._app.notificationService.displayPersistentMessage$(
                            `A support ticket has been created for this issue. The request id is ${ticket.issueKey}`,
                            "Dismiss"
                        );
                        this._dialogRef.close();
                    },
                    error: () => {
                        this.skipCheck = false;
                        this._app.notificationService.displayErrorMessage("Failed to submit ticket, please try again");
                    }
                });
        }
    }
}
